/**
 * Hostname-based runtime environment config.
 *
 * One Vercel build can serve both admin domains; we pick prod vs staging
 * URLs/keys from window.location.hostname at boot.
 *
 * Local/dev keeps the existing VITE_* vars. Production host prefers VITE_PROD_*
 * and falls back to VITE_* so existing Vercel env keeps working until both
 * prefixes are set.
 */

export type AppEnvName = 'production' | 'staging' | 'development'

export interface RuntimeConfig {
  appEnv: AppEnvName
  supabaseUrl: string
  supabaseAnonKey: string
  backendUrl: string
  scrapingApiUrl: string
  lighthouseAppUrl: string
}

const PROD_HOST = 'admin.joinlighthouse.ai'
const STAGING_HOST = 'staging-admin.joinlighthouse.ai'

function resolveEnvName(hostname: string): AppEnvName {
  if (hostname === STAGING_HOST) return 'staging'
  if (hostname === PROD_HOST) return 'production'
  return 'development'
}

function required(value: string | undefined, name: string): string {
  if (!value) {
    throw new Error(`Missing required env var: ${name}`)
  }
  return value
}

function firstDefined(...values: Array<string | undefined>): string | undefined {
  return values.find((v) => typeof v === 'string' && v.length > 0)
}

export function getRuntimeConfig(hostname = window.location.hostname): RuntimeConfig {
  const appEnv = resolveEnvName(hostname)

  if (appEnv === 'staging') {
    return {
      appEnv,
      supabaseUrl: required(
        import.meta.env.VITE_STAGING_SUPABASE_URL,
        'VITE_STAGING_SUPABASE_URL',
      ),
      supabaseAnonKey: required(
        import.meta.env.VITE_STAGING_SUPABASE_ANON_KEY,
        'VITE_STAGING_SUPABASE_ANON_KEY',
      ),
      backendUrl: required(
        import.meta.env.VITE_STAGING_BACKEND_URL,
        'VITE_STAGING_BACKEND_URL',
      ),
      scrapingApiUrl:
        import.meta.env.VITE_STAGING_SCRAPING_API_URL || 'http://localhost:8100',
      lighthouseAppUrl: import.meta.env.VITE_STAGING_LIGHTHOUSE_APP_URL || '',
    }
  }

  if (appEnv === 'production') {
    return {
      appEnv,
      supabaseUrl: required(
        firstDefined(
          import.meta.env.VITE_PROD_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_URL,
        ),
        'VITE_PROD_SUPABASE_URL or VITE_SUPABASE_URL',
      ),
      supabaseAnonKey: required(
        firstDefined(
          import.meta.env.VITE_PROD_SUPABASE_ANON_KEY,
          import.meta.env.VITE_SUPABASE_ANON_KEY,
        ),
        'VITE_PROD_SUPABASE_ANON_KEY or VITE_SUPABASE_ANON_KEY',
      ),
      backendUrl: required(
        firstDefined(
          import.meta.env.VITE_PROD_BACKEND_URL,
          import.meta.env.VITE_BACKEND_URL,
        ),
        'VITE_PROD_BACKEND_URL or VITE_BACKEND_URL',
      ),
      scrapingApiUrl:
        firstDefined(
          import.meta.env.VITE_PROD_SCRAPING_API_URL,
          import.meta.env.VITE_SCRAPING_API_URL,
        ) || 'http://localhost:8100',
      lighthouseAppUrl:
        firstDefined(
          import.meta.env.VITE_PROD_LIGHTHOUSE_APP_URL,
          import.meta.env.VITE_LIGHTHOUSE_APP_URL,
        ) || '',
    }
  }

  return {
    appEnv,
    supabaseUrl: required(import.meta.env.VITE_SUPABASE_URL, 'VITE_SUPABASE_URL'),
    supabaseAnonKey: required(
      import.meta.env.VITE_SUPABASE_ANON_KEY,
      'VITE_SUPABASE_ANON_KEY',
    ),
    backendUrl: required(import.meta.env.VITE_BACKEND_URL, 'VITE_BACKEND_URL'),
    scrapingApiUrl: import.meta.env.VITE_SCRAPING_API_URL || 'http://localhost:8100',
    lighthouseAppUrl: import.meta.env.VITE_LIGHTHOUSE_APP_URL || '',
  }
}

export const runtimeConfig = getRuntimeConfig()

export function isStagingEnv(): boolean {
  return runtimeConfig.appEnv === 'staging'
}

export function isProductionEnv(): boolean {
  return runtimeConfig.appEnv === 'production'
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]'])

/** Vite dev server on localhost — skip Lightbase login and treat the session as staff. */
export function isLocalDevAuthBypass(): boolean {
  return import.meta.env.DEV && LOCAL_HOSTS.has(window.location.hostname)
}
